import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate, Outlet } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Toaster } from '@/components/ui/sonner';
import {
  LayoutDashboard, Users, Building2, FileText, Shield,
  LogOut, ChevronLeft, Menu, X, UserCog, Clock, Mail, Zap, BarChart3
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const navItems = [
  { path: '/admin', label: 'Dashboard', icon: LayoutDashboard, exact: true },
  { path: '/admin/accounts', label: 'Accounts', icon: Building2 },
  { path: '/admin/reports', label: 'Reports', icon: BarChart3 },
  { path: '/admin/webhooks', label: 'Webhooks', icon: Shield },
  { path: '/admin/email', label: 'E-Mail', icon: Mail },
  { path: '/admin/email/triggers', label: 'Automatisierung', icon: Zap },
  { path: '/admin/audit-log', label: 'Audit-Log', icon: FileText },
];

export default function AdminLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [impersonationSession, setImpersonationSession] = useState(null);

  useEffect(() => {
    const storedUser = localStorage.getItem('cashx_user');
    if (storedUser) {
      const parsed = JSON.parse(storedUser);
      setUser(parsed);
      
      // Check if user has admin role
      if (parsed.role !== 'admin' && parsed.role !== 'super_admin') {
        toast.error('Zugriff verweigert', { description: 'Admin-Berechtigung erforderlich' });
        navigate('/app');
      }
    } else {
      navigate('/login');
    }
    
    // Check for active impersonation session
    const session = localStorage.getItem('cashx_impersonation');
    if (session) {
      setImpersonationSession(JSON.parse(session));
    }
  }, [navigate]);

  const handleLogout = () => {
    localStorage.removeItem('cashx_token');
    localStorage.removeItem('cashx_user');
    localStorage.removeItem('cashx_tenant');
    localStorage.removeItem('cashx_impersonation');
    toast.success('Erfolgreich abgemeldet');
    navigate('/login');
  };

  const handleEndImpersonation = async () => {
    if (!impersonationSession) return;
    
    const token = localStorage.getItem('cashx_token');
    try {
      await fetch(`${API_URL}/api/admin/impersonate/${impersonationSession.session_id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      // Restore admin token
      const adminToken = localStorage.getItem('cashx_admin_token');
      const adminUser = localStorage.getItem('cashx_admin_user');
      if (adminToken && adminUser) {
        localStorage.setItem('cashx_token', adminToken);
        localStorage.setItem('cashx_user', adminUser);
        localStorage.removeItem('cashx_admin_token');
        localStorage.removeItem('cashx_admin_user');
      }
      
      localStorage.removeItem('cashx_impersonation');
      setImpersonationSession(null);
      toast.success('Support-Session beendet');
      navigate('/admin/accounts');
    } catch (error) {
      toast.error('Fehler beim Beenden der Session');
    }
  };

  const isActive = (path, exact = false) => {
    if (exact) return location.pathname === path;
    return location.pathname.startsWith(path);
  };

  if (!user) return null;

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#0B1220', color: '#E8ECF2' }}>
      <Toaster position="top-center" />
      
      {/* Impersonation Banner */}
      {impersonationSession && (
        <div 
          data-testid="impersonation-banner"
          className="fixed top-0 left-0 right-0 z-50 bg-amber-500 text-black py-2 px-4"
        >
          <div className="max-w-7xl mx-auto flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Shield className="h-5 w-5" />
              <span className="font-medium">Support-Modus aktiv</span>
              <span className="text-sm">({impersonationSession.target_tenant?.company_name})</span>
              <Badge variant="secondary" className="bg-amber-600 text-white">
                <Clock className="h-3 w-3 mr-1" />
                {Math.round((new Date(impersonationSession.expires_at) - new Date()) / 60000)} Min
              </Badge>
            </div>
            <Button
              size="sm"
              variant="secondary"
              onClick={handleEndImpersonation}
              data-testid="end-impersonation-btn"
              className="bg-black text-white hover:bg-gray-800"
            >
              Session beenden
            </Button>
          </div>
        </div>
      )}
      
      {/* Mobile Header */}
      <header className="md:hidden fixed top-0 left-0 right-0 z-40 border-b border-slate-800" 
        style={{ backgroundColor: '#0E1628', marginTop: impersonationSession ? '40px' : 0 }}
      >
        <div className="flex items-center justify-between p-4">
          <button
            onClick={() => setIsMobileMenuOpen(true)}
            className="text-slate-400 hover:text-white"
          >
            <Menu className="h-6 w-6" />
          </button>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-[#2F80FF] rounded-lg flex items-center justify-center">
              <Shield className="h-4 w-4 text-white" />
            </div>
            <span className="font-semibold" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              Admin
            </span>
          </div>
          <div className="w-6" />
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div 
          className="md:hidden fixed inset-0 z-50 bg-black/60"
          onClick={() => setIsMobileMenuOpen(false)}
        >
          <motion.div
            initial={{ x: -280 }}
            animate={{ x: 0 }}
            exit={{ x: -280 }}
            className="w-72 h-full p-4"
            style={{ backgroundColor: '#0E1628' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-2">
                <div className="w-10 h-10 bg-[#2F80FF] rounded-lg flex items-center justify-center">
                  <Shield className="h-5 w-5 text-white" />
                </div>
                <span className="text-xl font-bold" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                  Admin
                </span>
              </div>
              <button onClick={() => setIsMobileMenuOpen(false)}>
                <X className="h-6 w-6 text-slate-400" />
              </button>
            </div>
            
            <nav className="space-y-1">
              {navItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${
                    isActive(item.path, item.exact)
                      ? 'bg-[#2F80FF] text-white'
                      : 'text-slate-400 hover:text-white hover:bg-slate-800/50'
                  }`}
                >
                  <item.icon className="h-5 w-5" />
                  {item.label}
                </Link>
              ))}
            </nav>
          </motion.div>
        </div>
      )}

      {/* Desktop Sidebar */}
      <aside 
        className={`hidden md:flex flex-col fixed left-0 h-full border-r border-slate-800 transition-all duration-300 z-30 ${
          isSidebarOpen ? 'w-64' : 'w-20'
        }`}
        style={{ backgroundColor: '#0E1628', top: impersonationSession ? '40px' : 0 }}
      >
        {/* Logo */}
        <div className="p-4 border-b border-slate-800">
          <Link to="/admin" className="flex items-center gap-3">
            <img 
              src="/cash-x-logo.png" 
              alt="Cash-X Admin" 
              className={isSidebarOpen ? "h-10 w-auto" : "h-8 w-auto"}
            />
            {isSidebarOpen && (
              <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30">
                Admin
              </Badge>
            )}
          </Link>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-1">
          {navItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              data-testid={`admin-nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${
                isActive(item.path, item.exact)
                  ? 'bg-[#2F80FF] text-white'
                  : 'text-slate-400 hover:text-white hover:bg-slate-800/50'
              }`}
              title={!isSidebarOpen ? item.label : undefined}
            >
              <item.icon className="h-5 w-5 flex-shrink-0" />
              {isSidebarOpen && <span>{item.label}</span>}
            </Link>
          ))}
        </nav>

        {/* User Info & Logout */}
        <div className="p-4 border-t border-slate-800">
          {isSidebarOpen && (
            <div className="mb-3 p-3 rounded-lg" style={{ backgroundColor: '#111C32' }}>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-slate-700 rounded-full flex items-center justify-center">
                  <UserCog className="h-5 w-5 text-slate-300" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{user?.name}</p>
                  <p className="text-xs text-slate-500 truncate">{user?.email}</p>
                  <Badge 
                    variant="outline" 
                    className={`text-xs mt-1 ${
                      user?.role === 'super_admin' 
                        ? 'border-amber-500 text-amber-400' 
                        : 'border-blue-500 text-blue-400'
                    }`}
                  >
                    {user?.role === 'super_admin' ? 'Super Admin' : 'Admin'}
                  </Badge>
                </div>
              </div>
            </div>
          )}
          
          <div className="flex gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsSidebarOpen(!isSidebarOpen)}
              className="text-slate-400 hover:text-white flex-shrink-0"
            >
              <ChevronLeft className={`h-5 w-5 transition-transform ${!isSidebarOpen ? 'rotate-180' : ''}`} />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleLogout}
              data-testid="admin-logout-btn"
              className={`text-slate-400 hover:text-white ${isSidebarOpen ? 'flex-1' : ''}`}
            >
              <LogOut className="h-5 w-5" />
              {isSidebarOpen && <span className="ml-2">Abmelden</span>}
            </Button>
          </div>
          
          {/* Back to App */}
          <Link to="/app">
            <Button
              variant="outline"
              size="sm"
              className="w-full mt-2 border-slate-700 text-slate-300 hover:bg-slate-800"
            >
              {isSidebarOpen ? 'Zurück zur App' : '←'}
            </Button>
          </Link>
        </div>
      </aside>

      {/* Main Content */}
      <main 
        className={`transition-all duration-300 ${
          isSidebarOpen ? 'md:ml-64' : 'md:ml-20'
        }`}
        style={{ paddingTop: impersonationSession ? '40px' : 0 }}
      >
        <div className="pt-16 md:pt-0 min-h-screen">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
